import React, { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle, X, Link2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useBulkImportAtivos } from '@/hooks/useBulkImportAtivos';
import { BulkImportResult, ImportValidationError } from '@/types/bulk-import';

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ImportStep = 'upload' | 'preview' | 'processing' | 'results';

export const BulkImportDialog = ({ open, onOpenChange }: BulkImportDialogProps) => {
  const { processImport, downloadTemplate, validateImportData, isProcessing } = useBulkImportAtivos();
  
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<ImportValidationError[]>([]);
  const [importResults, setImportResults] = useState<BulkImportResult | null>(null);
  const [createMissingRecords, setCreateMissingRecords] = useState(false);
  const [progress, setProgress] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      processFilePreview(file);
    }
  };

  const processFilePreview = async (file: File) => {
    try {
      const data = await parseFile(file);
      const validation = await validateImportData(data);
      
      setPreviewData(data);
      setValidationErrors(validation.errors);
      setCurrentStep('preview');
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
    }
  };

  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseFile = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n');
          const headers = parseCsvLine(lines[0]);
          
          const data = lines.slice(1)
            .filter(line => line.trim())
            .map((line, index) => {
              const values = parseCsvLine(line);
              const row: any = { _rowIndex: index + 2 };
              
              headers.forEach((header, i) => {
                row[header] = values[i] || '';
              });
              
              return row;
            });
          
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleStartImport = async () => {
    if (!selectedFile || !previewData.length) return;
    
    try {
      setCurrentStep('processing');
      setProgress(0);
      
      const result = await processImport(previewData, {
        createMissingRecords,
        onProgress: setProgress
      });
      
      setImportResults(result);
      setCurrentStep('results');
    } catch (error) {
      console.error('Erro na importação:', error);
    }
  };

  const resetDialog = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setImportResults(null);
    setProgress(0);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const relationshipCount = previewData.filter(r => r.relacionamento_destino?.trim()).length;

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
          <Upload className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Importar Ativos em Lote</h3>
        <p className="text-muted-foreground mb-6">
          Faça upload de um arquivo CSV com os dados dos ativos e seus relacionamentos
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Template de Importação
          </CardTitle>
          <CardDescription>
            Baixe o template com todas as colunas necessárias, incluindo relacionamentos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={() => downloadTemplate()} variant="outline" className="w-full">
            <Download className="h-4 w-4 mr-2" />
            Baixar Template CSV
          </Button>
          <div className="text-xs text-muted-foreground space-y-1">
            <p className="font-medium">Colunas de relacionamento (opcionais):</p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li><code className="text-xs bg-muted px-1 rounded">relacionamento_destino</code> — Nome do ativo destino</li>
              <li><code className="text-xs bg-muted px-1 rounded">relacionamento_tipo</code> — contains, depends_on, connects_to, hosts, uses, manages, monitors, backup_of, replaces, supports, part_of</li>
              <li><code className="text-xs bg-muted px-1 rounded">relacionamento_importancia</code> — critical, high, medium, low</li>
              <li><code className="text-xs bg-muted px-1 rounded">relacionamento_descricao</code> — Descrição do vínculo</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Upload do Arquivo</CardTitle>
          <CardDescription>
            Selecione o arquivo CSV com os dados dos ativos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground mb-2">
              Clique para selecionar o arquivo CSV
            </p>
            <Button onClick={() => fileInputRef.current?.click()}>
              Selecionar Arquivo
            </Button>
          </div>
          
          {selectedFile && (
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <Badge variant="secondary">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedFile(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Preview dos Dados</h3>
          <p className="text-muted-foreground">
            {previewData.length} ativo(s) • {relationshipCount} relacionamento(s)
          </p>
        </div>
        <Badge variant={validationErrors.length > 0 ? "destructive" : "default"}>
          {validationErrors.length} erro(s)
        </Badge>
      </div>

      {validationErrors.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Foram encontrados {validationErrors.length} erro(s) de validação. 
            Corrija os erros antes de prosseguir com a importação.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox
          id="create-missing"
          checked={createMissingRecords}
          onCheckedChange={(checked) => setCreateMissingRecords(checked === true)}
        />
        <label htmlFor="create-missing" className="text-sm">
          Criar automaticamente registros não encontrados
        </label>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados para Importação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Linha</th>
                  <th className="text-left p-2">Nome</th>
                  <th className="text-left p-2">Cliente</th>
                  <th className="text-left p-2">Relacionamento</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {previewData.slice(0, 20).map((row, index) => {
                  const rowErrors = validationErrors.filter(e => e.row === row._rowIndex);
                  const hasRelationship = !!row.relacionamento_destino?.trim();
                  return (
                    <tr key={index} className="border-b">
                      <td className="p-2">{row._rowIndex}</td>
                      <td className="p-2 font-medium">{row.nome}</td>
                      <td className="p-2">{row.cliente}</td>
                      <td className="p-2">
                        {hasRelationship ? (
                          <div className="flex items-center gap-1">
                            <Link2 className="h-3 w-3 text-primary" />
                            <span className="text-xs">{row.relacionamento_tipo} → {row.relacionamento_destino}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-2">
                        {rowErrors.length > 0 ? (
                          <Badge variant="destructive">Erro</Badge>
                        ) : (
                          <Badge variant="default">OK</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {validationErrors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-destructive">Detalhes dos Erros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-48 overflow-auto space-y-1">
              {validationErrors.map((error, i) => (
                <div key={i} className="text-xs p-2 bg-destructive/5 rounded">
                  <strong>Linha {error.row} [{error.field}]:</strong> {error.message}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center">
      <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
        <Upload className="h-6 w-6 text-primary animate-pulse" />
      </div>
      <div>
        <h3 className="text-lg font-semibold mb-2">Processando Importação</h3>
        <p className="text-muted-foreground mb-6">
          {progress < 70 
            ? 'Criando ativos...' 
            : 'Criando relacionamentos...'}
        </p>
      </div>
      
      <div className="space-y-2">
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-muted-foreground">{progress}% concluído</p>
      </div>
    </div>
  );

  const renderResultsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Importação Concluída</h3>
      </div>

      {importResults && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-green-600">
                {importResults.successful}
              </div>
              <p className="text-xs text-muted-foreground">Ativos Criados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-primary">
                {importResults.updated}
              </div>
              <p className="text-xs text-muted-foreground">Relacionamentos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-destructive">
                {importResults.failed}
              </div>
              <p className="text-xs text-muted-foreground">Falhas</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-2xl font-bold text-muted-foreground">
                {importResults.errors.length}
              </div>
              <p className="text-xs text-muted-foreground">Erros</p>
            </CardContent>
          </Card>
        </div>
      )}

      {importResults?.errors && importResults.errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Erros Encontrados</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-48 overflow-auto space-y-2">
              {importResults.errors.map((error, index) => (
                <div key={index} className="text-sm p-2 bg-destructive/5 rounded">
                  <strong>Linha {error.row} [{error.field}]:</strong> {error.message}
                  {error.value && <span className="text-muted-foreground ml-1">({error.value})</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importação em Lote de Ativos</DialogTitle>
          <DialogDescription>
            Importe múltiplos ativos e seus relacionamentos usando um arquivo CSV
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'preview' && renderPreviewStep()}
          {currentStep === 'processing' && renderProcessingStep()}
          {currentStep === 'results' && renderResultsStep()}
        </div>

        <DialogFooter>
          {currentStep === 'upload' && (
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
          )}
          
          {currentStep === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setCurrentStep('upload')}>
                Voltar
              </Button>
              <Button 
                onClick={handleStartImport}
                disabled={validationErrors.length > 0 || isProcessing}
              >
                Iniciar Importação
              </Button>
            </>
          )}
          
          {currentStep === 'results' && (
            <Button onClick={handleClose}>
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};